import { StripeWrapper } from "@qws-org/ui-kit";
import type { FC } from "react";
import type { LoaderFunction, MetaFunction } from "react-router";
import { data as responseData } from "react-router";
import type { ObjectSchema } from "yup";
import { object, string } from "yup";

import { useClientContext } from "~/contexts/client-context";
import { getExtensivePromotionById } from "~/core/deprecated/extensive-promotions";
import { getTopperBannerById } from "~/core/deprecated/header-banner";
import { getSpecialOffers } from "~/core/deprecated/special-offers";
import { getBreadcrumbsHomeLabel } from "~/core/transformers/breadcrumb-list.transformer";
import { prepareSeoData } from "~/core/transformers/seo.transformer";
import { PromoDescription } from "~/features/promo-description/PromoDescription";
import {
  Accordion,
  type AccordionProps,
} from "~/features-deprecated/accordion/Accordion";
import { TopperWithoutReg } from "~/features-deprecated/banner/TopperWithoutReg";
import { PromoPageSteps } from "~/features-deprecated/promoPageSteps/PromoPageSteps";
import { SpecialOffers } from "~/features-deprecated/special-offers/SpecialOffers";
import { Stripe } from "~/features-deprecated/stripe/Stripe";
import {
  featureFlagsContext,
  sdkContext,
  settingsContext,
} from "~/server/get-load-context";
import {
  createLcpImageLinkHeaders,
  resolveLcpImage,
} from "~/server/resolve-lcp-image.server";
import { STATIC_PAGE_CACHE_HEADERS } from "~/shared/cache-headers";
import { usePlayNowUrl } from "~/shared/utils/aff-integration";
import { getOriginalUrl } from "~/shared/utils/get-original-url";
import { useGtmContext } from "~/shared/utils/gtm-sdk/provider";
import { PageConstructor } from "~/widgets/page-constructor";
import { loadPagesByConstructor } from "~/widgets/pages-by-constructor/page-by-constructor.server";

import type { Route } from "./+types/$lang.$id";

const accordionData: AccordionProps = {
  data: [
    {
      title: { zh: "Terms and conditions", en: "Terms and conditions" },
      description: {
        zh: `- Winning a Lucky Pass package allows you to choose between various land-based events listed on LINK/URL. Do not play the satellites if you are unsure whether you can travel to any of the listed events or not. 
- The package value cannot be exchanged/reimbursed for money and/or tickets, however you are free to transfer your win to another Lucky  Poker registered player if that person fulfils the criterias listed below. 
- Packages will always include; 1 (one) buy-in to the Main Event (or other named side-event), accommodation for 1 (one) or 2 (two) persons on pre announced dates. In some cases a package can include a merchandise kit that may be handed over at the venue or sent to the winners registered address. The package holder must wear Lucky  Poker merchandise when playing the tournament, where appropriate 
- Packages will always include; 1 (one) buy-in to the Main Event (or other named side-event), accommodation for 1 (one) or 2 (two) persons on pre announced dates. In some cases a package can include a merchandise kit that may be handed over at the venue or sent to the winners registered address. The package holder must wear Lucky  Poker merchandise when playing the tournament, where appropriate `,
        en: `- Winning a Lucky Pass package allows you to choose between various land-based events listed on LINK/URL. Do not play the satellites if you are unsure whether you can travel to any of the listed events or not. 
- The package value cannot be exchanged/reimbursed for money and/or tickets, however you are free to transfer your win to another Lucky  Poker registered player if that person fulfils the criterias listed below. 
- Packages will always include; 1 (one) buy-in to the Main Event (or other named side-event), accommodation for 1 (one) or 2 (two) persons on pre announced dates. In some cases a package can include a merchandise kit that may be handed over at the venue or sent to the winners registered address. The package holder must wear Lucky  Poker merchandise when playing the tournament, where appropriate 
- Packages will always include; 1 (one) buy-in to the Main Event (or other named side-event), accommodation for 1 (one) or 2 (two) persons on pre announced dates. In some cases a package can include a merchandise kit that may be handed over at the venue or sent to the winners registered address. The package holder must wear Lucky  Poker merchandise when playing the tournament, where appropriate  `,
      },
      id: 1,
    },
  ],
};

const createDetailedTournamentValidator = (): ObjectSchema<{
  id: string;
  lang: string;
}> => {
  return object({
    lang: string().required(),
    id: string().required(),
  });
};

export const loader = (async ({ params, context, request }) => {
  const settings = context.get(settingsContext);
  const sdk = context.get(sdkContext);
  const featureFlags = context.get(featureFlagsContext);
  const { id, lang } = createDetailedTournamentValidator().validateSync(params);
  const isStaticSeriesPage = featureFlags.getItem("staticSeriesPage").enabled;
  const isStaticSeriesPageEnabled = id === "series" && isStaticSeriesPage;

  const pagesConstructorData = !isStaticSeriesPageEnabled
    ? await loadPagesByConstructor(sdk, id, lang, {
        slug: id,
        segment: null,
        pageList: "whenRelatedPages",
      })
    : undefined;

  const url = new URL(request.url);
  const cmsPath = `${url.protocol}//${url.host}`;
  const lcpImage = resolveLcpImage(pagesConstructorData?.page, cmsPath);
  const responseHeaders = new Headers(STATIC_PAGE_CACHE_HEADERS);

  for (const linkHeader of createLcpImageLinkHeaders(lcpImage)) {
    responseHeaders.append("Link", linkHeader);
  }

  return responseData(
    {
      pageData: pagesConstructorData?.page,
      pages: pagesConstructorData?.pages,
      originalUrl: getOriginalUrl(request.url),
      languages: settings?.languages,
      topperBanner: isStaticSeriesPageEnabled
        ? getTopperBannerById("series")
        : undefined,
      specialOffers: isStaticSeriesPageEnabled ? getSpecialOffers() : undefined,
      extensivePromotion: isStaticSeriesPageEnabled
        ? getExtensivePromotionById("series")
        : undefined,
      featureFlags: {
        isSpecialOffersButtonEnabled: featureFlags.getItem(
          "specialOffersButton",
        ).enabled,
        isSupportIconsEnabled:
          featureFlags.getItem("supportIconsBlock").enabled,
        isAccordionEnabled: featureFlags.getItem("seriesPageAccordion").enabled,
        isStaticSeriesPageEnabled,
        isNavigationEnabled: featureFlags.getItem("headerNavigationLinks")
          .enabled,
        isLayoutV2Enabled: featureFlags.getItem("layouts_v2").enabled,
      },
      lcpImage,
    },
    {
      headers: responseHeaders,
    },
  );
}) satisfies LoaderFunction;

export function headers({
  loaderHeaders,
  actionHeaders,
}: Route.HeadersArgs): Headers {
  return loaderHeaders ?? actionHeaders;
}

export const meta: MetaFunction<typeof loader> = ({
  data,
  params,
  matches,
}) => {
  return prepareSeoData({
    pageSeo: data?.pageData?.page.seo,
    fallbackSeo: data?.pageData?.fallbackSeo?.seo,
    originalUrl: `${data?.originalUrl}`,
    currentLang: params?.lang ?? "",
    languages: data?.languages ?? [],
    matches,
    breadcrumbs: data?.pageData
      ? {
          data: data.pageData,
          homeLabel: getBreadcrumbsHomeLabel(matches),
        }
      : undefined,
  });
};

const Page: FC<Route.ComponentProps> = ({
  loaderData: {
    specialOffers,
    featureFlags,
    extensivePromotion,
    topperBanner,
    pageData,
    pages,
  },
}) => {
  const { playerID, language } = useClientContext();
  const { gtm } = useGtmContext();
  const playNowUrl = usePlayNowUrl();

  return (
    <>
      {featureFlags.isStaticSeriesPageEnabled ? (
        <div className="main-page series">
          <TopperWithoutReg
            topperTitle={topperBanner?.topperTitle[language]}
            isMainTopper={topperBanner?.isMain}
            playNowLink={playNowUrl}
            topperBackground={{
              imageVariants: topperBanner?.imageDesktop[language],
              mobileImageVariants: topperBanner?.imageMobile[language],
            }}
            analytics={{
              createAccountMain() {
                gtm.createAccountMain({
                  eventTime: new Date(),
                  playerID,
                  screenName: "mainPage",
                });
              },
              logInMain() {
                gtm.logInMain({
                  eventTime: new Date(),
                  playerID,
                  screenName: "mainPage",
                });
              },
            }}
          />
          <StripeWrapper variant="secondary">
            <Stripe title="Launch yourself into Poker Stardom!" />
          </StripeWrapper>
          {extensivePromotion && (
            <PromoDescription data={extensivePromotion} lang={language} />
          )}
          <StripeWrapper variant="tertiary">
            <PromoPageSteps
              firstStepText="Quick Registration"
              secondStepText="Play Mini-game"
              thirdStepText="Deposit to get rewards"
              playNowText="Play Now"
              playNowLink={playNowUrl}
              analytics={{
                playNow() {
                  gtm.playNow({
                    eventTime: new Date(),
                    playerID,
                    screenName: "mainPage",
                  });
                },
              }}
            />
          </StripeWrapper>
          {specialOffers && (
            <SpecialOffers
              featureFlags={featureFlags}
              specialOffers={specialOffers}
            />
          )}
          {featureFlags.isAccordionEnabled && (
            <Accordion data={accordionData.data} />
          )}
        </div>
      ) : (
        pageData && <PageConstructor data={pageData} pages={pages} />
      )}
    </>
  );
};

export default Page;
